/**
 * F11 Task 19 — Admin B2B Account detail page.
 *
 * Route: /b2b-accounts/:id
 * Permission: b2b_accounts:read
 *
 * Header: account display name + legal name + status badge.
 * Action bar: Suspend/Resume + "Reassign owner of a venue".
 *
 * Tabs:
 *   - Overview        : read-only business details + inline edit (PATCH)
 *   - Venues          : table of owned venues; per-row Reassign dialog
 *   - Claims          : table of claims; row → /venue-claims/:id
 *   - Audit log       : admin_audit_log rows scoped to this target
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  Avatar,
  Badge,
  type BadgeTone,
  Banner,
  Button,
  Card,
  EmptyState,
  FormField,
  Modal,
  SearchInput,
  Spinner,
  StatusChip,
  type StatusChipStatus,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Table,
  type TableColumn,
  TextInput,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import {
  ReassignVenueOwnershipInputSchema,
  UpdateB2bAccountInputSchema,
  type AdminAuditEntry,
  type B2bAccount,
  type B2bAccountStatus,
  type EntityVerificationSummary,
  type NameLockOverride,
  type ReassignVenueOwnershipInput,
  type UpdateB2bAccountInput,
  type Venue,
  type VenueClaim,
} from '@bravemove/types';
import {
  adminB2bAccountApi,
  type B2bAccountDetailResponse,
  type B2bAccountListItem,
} from '../../api/b2b-account.js';
import { translateApiError } from '../../auth/errors.js';
import { adminFetch, buildQuery } from '../../auth/apiClient.js';
import type { ListEnvelope } from '../../auth/types.js';
import { useAuth } from '../../auth/useAuth.js';
import { adminVerificationApi } from '../../api/verification';
import {
  EntityVerificationCard,
  InertTick,
} from '../../components/verification/EntityVerificationCard';
import { NameLockOverridePanel } from '../../components/verification/NameLockOverride';
import { isNameLockedError } from '../../components/verification/verificationErrors';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import { B2bAccountMembersTab } from './B2bAccountMembersTab.js';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_TONE: Record<B2bAccountStatus, BadgeTone> = {
  active: 'success',
  suspended: 'warning',
  disabled: 'danger',
};

const CLAIM_STATUS_CHIP_MAP: Record<string, StatusChipStatus> = {
  draft: 'pending',
  pending: 'in_review',
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'pending',
  revoked_by_admin: 'rejected',
};

/** Tab keys synced to the `?tab=` query param so a refresh keeps the tab open. */
const B2B_TABS = ['overview', 'venues', 'claims', 'audit', 'members'] as const;

/** Badge tone for the F07 `users.status` shown on the primary-owner card. */
const USER_STATUS_TONE: Record<string, BadgeTone> = {
  active: 'success',
  pending_verification: 'warning',
  disabled: 'danger',
  deleted: 'neutral',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

// ─── Suspend dialog ──────────────────────────────────────────────────────────

interface SuspendFormValues {
  readonly reason: string;
}

function SuspendDialog({
  open,
  loading,
  serverError,
  onClose,
  onSubmit,
}: {
  open: boolean;
  loading: boolean;
  serverError: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const { t } = useI18n();
  const schema = z.object({
    reason: z.string().min(10).max(500),
  });
  const form = useForm<SuspendFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) form.reset({ reason: '' });
  }, [open, form]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:claim.b2b_account_detail.suspend_dialog.title')}
      description={t('admin:claim.b2b_account_detail.suspend_dialog.body')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:claim.b2b_account_detail.suspend_dialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={loading}
            onClick={() => void form.handleSubmit((v) => onSubmit(v.reason))()}
          >
            {t('admin:claim.b2b_account_detail.suspend_dialog.confirm')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit((v) => onSubmit(v.reason))();
        }}
        className="space-y-4"
      >
        <FormField
          label={t('admin:claim.b2b_account_detail.suspend_dialog.reason_label')}
          helper={t('admin:claim.b2b_account_detail.suspend_dialog.reason_helper')}
          error={form.formState.errors.reason?.message}
          required
        >
          <Textarea rows={4} maxLength={500} showCounter {...form.register('reason')} />
        </FormField>
        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}

// ─── Resume dialog ───────────────────────────────────────────────────────────

interface ResumeFormValues {
  readonly note: string;
}

function ResumeDialog({
  open,
  loading,
  serverError,
  onClose,
  onSubmit,
}: {
  open: boolean;
  loading: boolean;
  serverError: string | null;
  onClose: () => void;
  onSubmit: (note: string) => void;
}) {
  const { t } = useI18n();
  const schema = z.object({
    note: z.string().max(500),
  });
  const form = useForm<ResumeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { note: '' },
  });

  useEffect(() => {
    if (open) form.reset({ note: '' });
  }, [open, form]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:claim.b2b_account_detail.resume_dialog.title')}
      description={t('admin:claim.b2b_account_detail.resume_dialog.body')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:claim.b2b_account_detail.resume_dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={loading}
            onClick={() => void form.handleSubmit((v) => onSubmit(v.note))()}
          >
            {t('admin:claim.b2b_account_detail.resume_dialog.confirm')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit((v) => onSubmit(v.note))();
        }}
        className="space-y-4"
      >
        <FormField label={t('admin:claim.b2b_account_detail.resume_dialog.note_label')}>
          <Textarea rows={3} maxLength={500} showCounter {...form.register('note')} />
        </FormField>
        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}

// ─── Reassign owner dialog ───────────────────────────────────────────────────

interface ReassignFormValues {
  readonly newOwnerB2bAccountId: string | null;
  readonly reason: string;
  readonly clearOwnership: boolean;
}

function ReassignOwnerDialog({
  open,
  venueName,
  currentAccountId,
  loading,
  serverError,
  onClose,
  onSubmit,
}: {
  open: boolean;
  venueName: string;
  currentAccountId: string;
  loading: boolean;
  serverError: string | null;
  onClose: () => void;
  onSubmit: (body: ReassignVenueOwnershipInput) => void;
}) {
  const { t } = useI18n();
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState<readonly B2bAccountListItem[]>([]);
  const [searching, setSearching] = useState(false);

  // Use a relaxed local schema — we handle the conditional `newOwnerB2bAccountId`
  // via the form's `clearOwnership` toggle before calling the strict server schema.
  const schema = z.object({
    newOwnerB2bAccountId: z.string().uuid().nullable(),
    reason: z.string().min(10).max(500),
    clearOwnership: z.boolean(),
  });

  const form = useForm<ReassignFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newOwnerB2bAccountId: null, reason: '', clearOwnership: false },
  });

  useEffect(() => {
    if (open) {
      form.reset({ newOwnerB2bAccountId: null, reason: '', clearOwnership: false });
      setSearchValue('');
      setResults([]);
    }
  }, [open, form]);

  // Debounce account search.
  useEffect(() => {
    if (!open) return undefined;
    if (form.watch('clearOwnership')) return undefined;
    if (!searchValue.trim()) {
      setResults([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminB2bAccountApi.listAccounts({
          q: searchValue.trim(),
          status: 'active',
          limit: 10,
        });
        // Hide the current account from the picker — moving it to itself is a no-op.
        setResults(res.items.filter((r) => r.id !== currentAccountId));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchValue, open, currentAccountId, form]);

  const clearOwnership = form.watch('clearOwnership');
  const selectedAccountId = form.watch('newOwnerB2bAccountId');

  const handleSubmit = form.handleSubmit((values) => {
    const newOwner = values.clearOwnership ? null : values.newOwnerB2bAccountId;
    // Validate against server schema before submitting.
    const parsed = ReassignVenueOwnershipInputSchema.safeParse({
      newOwnerB2bAccountId: newOwner,
      reason: values.reason,
    });
    if (!parsed.success) {
      form.setError('reason', { message: parsed.error.issues[0]?.message ?? 'Invalid' });
      return;
    }
    onSubmit(parsed.data);
  });

  const canConfirm = clearOwnership || (selectedAccountId !== null && selectedAccountId !== '');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:claim.b2b_account_detail.reassign_owner_dialog.title', {
        venueName,
        defaultValue: `Reassign owner of ${venueName}`,
      })}
      description={t('admin:claim.b2b_account_detail.reassign_owner_dialog.body')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:claim.b2b_account_detail.reassign_owner_dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={loading}
            disabled={!canConfirm}
            onClick={() => void handleSubmit()}
          >
            {t('admin:claim.b2b_account_detail.reassign_owner_dialog.confirm')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        className="space-y-4"
      >
        {/* Clear ownership toggle */}
        <label className="flex items-start gap-2 cursor-pointer rounded-md border border-hairline-2 p-3">
          <input
            type="checkbox"
            checked={clearOwnership}
            onChange={(e) => form.setValue('clearOwnership', e.target.checked)}
            className="mt-0.5"
          />
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-medium text-ink">
              {t('admin:claim.b2b_account_detail.reassign_owner_dialog.clear_ownership_label')}
            </span>
            <span className="text-[11.5px] text-ink-3">
              {t('admin:claim.b2b_account_detail.reassign_owner_dialog.clear_ownership_helper')}
            </span>
          </div>
        </label>

        {!clearOwnership ? (
          <FormField
            label={t('admin:claim.b2b_account_detail.reassign_owner_dialog.account_picker_label')}
            helper={t('admin:claim.b2b_account_detail.reassign_owner_dialog.account_picker_helper')}
            required
          >
            <SearchInput
              placeholder={t('admin:claim.b2b_accounts.search_placeholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {searching ? (
              <div className="pt-2">
                <Spinner size={16} />
              </div>
            ) : results.length > 0 ? (
              <ul
                role="listbox"
                aria-label={t(
                  'admin:claim.b2b_account_detail.reassign_owner_dialog.account_picker_label',
                )}
                className="mt-2 max-h-48 overflow-y-auto rounded-md border border-hairline-2 divide-y divide-hairline-2"
              >
                {results.map((account) => (
                  <li
                    key={account.id}
                    role="option"
                    aria-selected={selectedAccountId === account.id}
                    onClick={() => form.setValue('newOwnerB2bAccountId', account.id)}
                    className={`cursor-pointer px-3 py-2 text-[12.5px] ${
                      selectedAccountId === account.id
                        ? 'bg-accent text-accent-ink'
                        : 'hover:bg-surface-2'
                    }`}
                  >
                    <div className="font-medium">{account.displayName}</div>
                    <div className="text-[11px] opacity-80">{account.legalBusinessName}</div>
                  </li>
                ))}
              </ul>
            ) : searchValue.trim() ? (
              <div className="pt-2 text-[12px] text-ink-3">
                {t('admin:claim.b2b_account_detail.reassign_owner_dialog.no_matches')}
              </div>
            ) : null}
          </FormField>
        ) : null}

        <FormField
          label={t('admin:claim.b2b_account_detail.reassign_owner_dialog.reason_label')}
          helper={t('admin:claim.b2b_account_detail.reassign_owner_dialog.reason_helper')}
          error={form.formState.errors.reason?.message}
          required
        >
          <Textarea rows={3} maxLength={500} showCounter {...form.register('reason')} />
        </FormField>

        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}

// ─── Overview tab — Edit form ────────────────────────────────────────────────

function OverviewEditForm({
  account,
  saving,
  serverError,
  nameLockError,
  canOverrideNameLock,
  onCancel,
  onSave,
  onNameLockDismiss,
}: {
  account: B2bAccount;
  saving: boolean;
  serverError: string | null;
  /** F09 v2 — the last save's 422 `NAME_LOCKED_VERIFIED`, or `null`. */
  nameLockError: unknown;
  /** The admin holds `verifications:review` (the override control shows only then). */
  canOverrideNameLock: boolean;
  onCancel: () => void;
  /** Save the patch; `nameLockOverride` is set only by the override control. */
  onSave: (patch: UpdateB2bAccountInput, nameLockOverride?: NameLockOverride) => void;
  /** Drop the lock notice (a name edit, or the panel's own cancel). */
  onNameLockDismiss: () => void;
}) {
  const { t } = useI18n();
  const form = useForm<UpdateB2bAccountInput>({
    resolver: zodResolver(UpdateB2bAccountInputSchema),
    defaultValues: {
      legalBusinessName: account.legalBusinessName,
      displayName: account.displayName,
      countryCode: account.countryCode,
      taxId: account.taxId ?? '',
    },
  });

  // The patch is ALWAYS built from the live form values, both on a plain save
  // and on a name-lock override, so an override never resubmits a stale
  // snapshot of what the admin typed before the 422 (mirrors UserEditForm /
  // VenueEditForm, which call `collectChanges()` inside the override path).
  const submitWith = (nameLockOverride?: NameLockOverride) =>
    form.handleSubmit((values) => {
      const patch: UpdateB2bAccountInput = {};
      if (values.legalBusinessName && values.legalBusinessName !== account.legalBusinessName)
        patch.legalBusinessName = values.legalBusinessName;
      if (values.displayName && values.displayName !== account.displayName)
        patch.displayName = values.displayName;
      if (values.countryCode && values.countryCode !== account.countryCode)
        patch.countryCode = values.countryCode;
      const newTaxId = (values.taxId ?? '').trim();
      if (newTaxId !== (account.taxId ?? '')) {
        // `null` explicitly CLEARS the tax id (identity-f09-f11-002) — `undefined`
        // would be dropped by JSON.stringify and the clear would silently no-op.
        patch.taxId = newTaxId.length > 0 ? newTaxId : null;
      }
      onSave(patch, nameLockOverride);
    })();

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submitWith();
        }}
        className="space-y-4"
      >
        <FormField
          label={t('admin:claim.b2b_account_detail.field_legal_name')}
          error={form.formState.errors.legalBusinessName?.message}
          required
        >
          {/* A new name needs a new lock decision. */}
          <TextInput {...form.register('legalBusinessName', { onChange: onNameLockDismiss })} />
        </FormField>
        <FormField
          label={t('admin:claim.b2b_account_detail.field_display_name')}
          error={form.formState.errors.displayName?.message}
          required
        >
          <TextInput {...form.register('displayName', { onChange: onNameLockDismiss })} />
        </FormField>
        <FormField
          label={t('admin:claim.b2b_account_detail.field_country')}
          error={form.formState.errors.countryCode?.message}
        >
          <TextInput {...form.register('countryCode')} maxLength={2} />
        </FormField>
        <FormField
          label={t('admin:claim.b2b_account_detail.field_tax_id')}
          error={form.formState.errors.taxId?.message}
        >
          <TextInput {...form.register('taxId')} />
        </FormField>
        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" type="button" onClick={onCancel}>
            {t('admin:claim.b2b_account_detail.cancel_cta')}
          </Button>
          <Button variant="primary" type="submit" loading={saving}>
            {t('admin:claim.b2b_account_detail.save_cta')}
          </Button>
        </div>
      </form>
      {nameLockError !== null ? (
        <NameLockOverridePanel
          error={nameLockError}
          canOverride={canOverrideNameLock}
          busy={saving}
          onOverride={(override) => void submitWith(override)}
          onDismiss={onNameLockDismiss}
          testId="b2b-account-name-lock"
        />
      ) : null}
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function B2bAccountDetailPage() {
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const auth = useAuth();
  // F13 T26 — Members tab visibility gated by `b2b_account_members:read`
  // (granted to super_admin + support only). Finance + content_moderator
  // never see the tab.
  const canViewMembers = auth.adminUser?.permissions.includes('b2b_account_members:read') ?? false;
  // The "View user" deep-link targets the F43 user-detail page (`/users/:id`),
  // gated on `users:read` (every admin role holds it — defensive hide otherwise).
  const canViewUsers = auth.adminUser?.permissions.includes('users:read') ?? false;
  // identity-f09-f11-006 — the page route is gated on `b2b_accounts:read`
  // (all 4 roles), but Suspend/Resume/Edit hit endpoints gated on
  // `b2b_accounts:write` and Reassign hits `venues:write` (super_admin +
  // content_moderator only). Hide those affordances for read-only actors so
  // support/finance stop getting server 403s on submit.
  const canWriteAccounts = auth.adminUser?.permissions.includes('b2b_accounts:write') ?? false;
  const canReassignVenues = auth.adminUser?.permissions.includes('venues:write') ?? false;
  // identity-f09-f11-008 — GET /admin/audit-log pins non-super_admin actors to
  // their OWN actions server-side; surface that scoping on the audit tab.
  const auditScopedToSelf = auth.adminUser !== null && auth.adminUser.role !== 'super_admin';
  // F09 v2 — the verification card + deep links need `verifications:read`; the
  // name-lock override needs `verifications:review` (on top of the write perm).
  const { canRead: canReadVerifications, canReview: canOverrideNameLock } =
    useVerificationPermissions();

  // Tab state synced to `?tab=` so a refresh restores the open tab (mirrors the
  // F42 venue-detail page). A `members` deep-link falls back to overview when
  // the actor can't read members.
  const tabParam = searchParams.get('tab') ?? '';
  const requestedTab = (B2B_TABS as readonly string[]).includes(tabParam) ? tabParam : 'overview';
  const activeTab = requestedTab === 'members' && !canViewMembers ? 'overview' : requestedTab;
  const handleTabChange = (v: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', v);
        return next;
      },
      { replace: true },
    );
  };

  const [detail, setDetail] = useState<B2bAccountDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline edit (Overview tab).
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // F09 v2 — the last save hit a verified account's name (422
  // `NAME_LOCKED_VERIFIED`). Only the error is kept: the override resubmits the
  // form's CURRENT values, never a snapshot taken at the 422.
  const [nameLockError, setNameLockError] = useState<unknown>(null);

  // F09 v2 — this account's verification summary (club / organizer accounts),
  // keyed by `<accountId>:<reload>` so a stale fetch never paints another
  // account (or a retried load). `failed` keeps a fetch error apart from "this
  // account has no club / organizer entity".
  const [verification, setVerification] = useState<{
    readonly key: string;
    readonly summary: EntityVerificationSummary | null;
    readonly failed: boolean;
  } | null>(null);
  const [verificationReload, setVerificationReload] = useState(0);

  // Suspend / Resume dialog state.
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [suspendError, setSuspendError] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);

  // Reassign-owner dialog state (targeted at a specific venue).
  const [reassignVenue, setReassignVenue] = useState<Venue | null>(null);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);

  // Audit log tab data.
  const [auditRows, setAuditRows] = useState<readonly AdminAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminB2bAccountApi.getAccountDetail(id);
      setDetail(data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchAudit = useCallback(async () => {
    if (!id) return;
    setAuditLoading(true);
    setAuditError(null);
    try {
      const body = await adminFetch<ListEnvelope<AdminAuditEntry>>(
        `/admin/audit-log${buildQuery({
          targetType: 'b2b_account',
          targetId: id,
          limit: 50,
        })}`,
      );
      setAuditRows(body.items);
    } catch (err) {
      setAuditError(translateApiError(err, t));
    } finally {
      setAuditLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  // F09 v2 (E14) — the account's verification summary comes from the owner's
  // entity list (`GET /admin/verification-entities?ownerUserId=`); only club /
  // organizer accounts can hold a tick, so a venue-kind account finds none.
  const verificationAccountId = detail?.account.id ?? null;
  const verificationOwnerId = detail?.account.primaryOwnerUserId ?? null;
  const verificationKey =
    verificationAccountId !== null ? `${verificationAccountId}:${verificationReload}` : null;
  useEffect(() => {
    if (!canReadVerifications || !verificationAccountId || !verificationOwnerId) return;
    if (verificationKey === null) return;
    let alive = true;
    adminVerificationApi
      .entitiesForOwner(verificationOwnerId)
      .then((rows) => {
        if (!alive) return;
        const summary =
          rows.find(
            (r) =>
              r.entityId === verificationAccountId &&
              (r.entityType === 'club' || r.entityType === 'organizer'),
          ) ?? null;
        setVerification({ key: verificationKey, summary, failed: false });
      })
      .catch(() => {
        // A failed load is NOT "no club / organizer entity": surface it with a
        // retry (mirrors UserVerificationsCard) instead of hiding the card.
        if (alive) setVerification({ key: verificationKey, summary: null, failed: true });
      });
    return () => {
      alive = false;
    };
  }, [canReadVerifications, verificationAccountId, verificationOwnerId, verificationKey]);

  // Lazy-load the audit log whenever the audit tab is active — covers both a
  // click (URL → activeTab) and a cold refresh landing on `?tab=audit`.
  useEffect(() => {
    if (activeTab !== 'audit') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchAudit();
  }, [activeTab, fetchAudit]);

  const handleSave = async (patch: UpdateB2bAccountInput, nameLockOverride?: NameLockOverride) => {
    if (!detail) return;
    setSaving(true);
    setEditError(null);
    try {
      await adminB2bAccountApi.updateAccount(
        detail.account.id,
        nameLockOverride ? { ...patch, nameLockOverride } : patch,
      );
      setNameLockError(null);
      toast.push({
        tone: 'success',
        title: t('admin:claim.b2b_account_detail.edit_success'),
      });
      setEditing(false);
      void fetchDetail();
    } catch (err) {
      // F09 v2 — a verified account's name is locked: offer the override.
      if (isNameLockedError(err)) setNameLockError(err);
      else setEditError(translateApiError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async (reason: string) => {
    if (!detail) return;
    setSuspendLoading(true);
    setSuspendError(null);
    try {
      await adminB2bAccountApi.suspendAccount(detail.account.id, { reason });
      toast.push({
        tone: 'success',
        title: t('admin:claim.b2b_account_detail.suspend_dialog.confirm'),
      });
      setSuspendOpen(false);
      void fetchDetail();
    } catch (err) {
      setSuspendError(translateApiError(err, t));
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleResume = async (note: string) => {
    if (!detail) return;
    setResumeLoading(true);
    setResumeError(null);
    try {
      const body = note.trim() ? { note: note.trim() } : {};
      await adminB2bAccountApi.resumeAccount(detail.account.id, body);
      toast.push({
        tone: 'success',
        title: t('admin:claim.b2b_account_detail.resume_dialog.confirm'),
      });
      setResumeOpen(false);
      void fetchDetail();
    } catch (err) {
      setResumeError(translateApiError(err, t));
    } finally {
      setResumeLoading(false);
    }
  };

  const handleReassign = async (body: ReassignVenueOwnershipInput) => {
    if (!reassignVenue) return;
    setReassignLoading(true);
    setReassignError(null);
    try {
      await adminB2bAccountApi.reassignVenueOwner(reassignVenue.id, body);
      toast.push({
        tone: 'success',
        title: t('admin:claim.b2b_account_detail.reassign_owner_dialog.success'),
      });
      setReassignVenue(null);
      void fetchDetail();
    } catch (err) {
      setReassignError(translateApiError(err, t));
    } finally {
      setReassignLoading(false);
    }
  };

  // Build venue table columns. The Reassign action column is rendered only for
  // actors holding `venues:write` (identity-f09-f11-006).
  const venueColumns = useMemo<readonly TableColumn<Venue>[]>(
    () => [
      {
        key: 'name',
        header: t('admin:claim.b2b_account_detail.venues_tab.column_name'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-[13px] text-ink">{row.name}</span>
            <span className="font-mono text-[11px] text-ink-3">{row.slug}</span>
          </div>
        ),
      },
      {
        key: 'tier',
        header: t('admin:claim.b2b_account_detail.venues_tab.column_tier'),
        cell: (row) => (
          <Badge tone={row.tier === 'tier_2' ? 'success' : 'info'} size="sm">
            {row.tier}
          </Badge>
        ),
      },
      {
        key: 'claimStatus',
        header: t('admin:claim.b2b_account_detail.venues_tab.column_claim_status'),
        cell: (row) => (
          <Badge tone={row.claimStatus === 'claimed' ? 'success' : 'neutral'} size="sm">
            {row.claimStatus}
          </Badge>
        ),
      },
      ...(canReassignVenues
        ? [
            {
              key: 'actions',
              header: t('admin:claim.b2b_account_detail.venues_tab.column_actions'),
              align: 'end',
              cell: (row) => (
                <Button size="xs" variant="ghost" onClick={() => setReassignVenue(row)}>
                  {t('admin:claim.b2b_account_detail.venues_tab.reassign_cta')}
                </Button>
              ),
            } satisfies TableColumn<Venue>,
          ]
        : []),
    ],
    [t, canReassignVenues],
  );

  // Build claim table columns.
  const claimColumns = useMemo<readonly TableColumn<VenueClaim>[]>(
    () => [
      {
        key: 'venue',
        header: t('admin:claim.b2b_account_detail.claims_tab.column_venue'),
        cell: (row) => (
          <span className="font-mono text-[12px] text-ink-2">{row.venueId.slice(0, 8)}…</span>
        ),
      },
      {
        key: 'status',
        header: t('admin:claim.b2b_account_detail.claims_tab.column_status'),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={CLAIM_STATUS_CHIP_MAP[row.status] ?? 'pending'}
            label={t(
              `admin:claim.queue.filter_status_${row.status === 'revoked_by_admin' ? 'revoked' : row.status}`,
            )}
          />
        ),
      },
      {
        key: 'submitted',
        header: t('admin:claim.b2b_account_detail.claims_tab.column_submitted'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">{formatDate(row.submittedAt, locale)}</span>
        ),
      },
      {
        key: 'decided',
        header: t('admin:claim.b2b_account_detail.claims_tab.column_decided'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">{formatDate(row.decidedAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  // Build audit table columns.
  const auditColumns = useMemo<readonly TableColumn<AdminAuditEntry>[]>(
    () => [
      {
        key: 'action',
        header: t('admin:claim.b2b_account_detail.audit_tab.column_action'),
        cell: (row) => <span className="font-mono text-[12px] text-ink">{row.action}</span>,
      },
      {
        key: 'actor',
        header: t('admin:claim.b2b_account_detail.audit_tab.column_actor'),
        cell: (row) => <span className="text-[12.5px] text-ink-2">{row.actorEmail}</span>,
      },
      {
        key: 'when',
        header: t('admin:claim.b2b_account_detail.audit_tab.column_when'),
        cell: (row) => (
          <span className="text-[12px] text-ink-3">{formatDate(row.createdAt, locale)}</span>
        ),
      },
      {
        key: 'status',
        header: t('admin:claim.b2b_account_detail.audit_tab.column_status'),
        cell: (row) => (
          <Badge tone={row.status === 'success' ? 'success' : 'danger'} size="sm">
            {row.status}
          </Badge>
        ),
      },
    ],
    [t, locale],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Banner tone="danger">{error ?? t('admin:errors.generic')}</Banner>
        <Button variant="ghost" onClick={() => navigate('/b2b-accounts')}>
          {t('admin:claim.b2b_account_detail.back_to_list')}
        </Button>
      </div>
    );
  }

  const { account, primaryOwner, venues, claims } = detail;
  const isSuspended = account.status === 'suspended';
  // F09 v2 — the account tick (2-valued entity column) + its F09 summary.
  const accountVerified = account.verificationStatus === 'verified';
  const verificationLoad =
    verification !== null && verification.key === `${account.id}:${verificationReload}`
      ? verification
      : null;
  const verificationSummary = verificationLoad?.summary ?? null;
  const verificationFailed = verificationLoad?.failed === true;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <button
          type="button"
          className="text-[12px] text-accent hover:underline underline-offset-2"
          onClick={() => navigate('/b2b-accounts')}
        >
          &larr; {t('admin:claim.b2b_account_detail.back_to_list')}
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="inline-flex items-center gap-2 text-h1 font-bold text-ink">
              {account.displayName}
              <InertTick
                verified={accountVerified}
                entityType={verificationSummary?.entityType}
                size="md"
                testId="b2b-account-tick"
              />
            </h1>
            <p className="text-[13px] text-ink-3">{account.legalBusinessName}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone={STATUS_TONE[account.status]} size="sm">
                {t(`admin:claim.b2b_accounts.filter_status_${account.status}`)}
              </Badge>
              {account.nameLocked === true ? (
                <Badge tone="neutral" size="sm" data-testid="b2b-account-name-locked">
                  {t('admin:verification.entity.nameLocked')}
                </Badge>
              ) : null}
            </div>
          </div>
          {canWriteAccounts ? (
            <div className="flex flex-wrap gap-2">
              {isSuspended ? (
                <Button variant="primary" onClick={() => setResumeOpen(true)}>
                  {t('admin:claim.b2b_account_detail.resume_cta')}
                </Button>
              ) : (
                <Button variant="destructive" onClick={() => setSuspendOpen(true)}>
                  {t('admin:claim.b2b_account_detail.suspend_cta')}
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <Tabs value={activeTab} defaultValue="overview" onValueChange={handleTabChange}>
        <TabList>
          <Tab value="overview">{t('admin:claim.b2b_account_detail.tab_overview')}</Tab>
          <Tab value="venues">{t('admin:claim.b2b_account_detail.tab_venues')}</Tab>
          <Tab value="claims">{t('admin:claim.b2b_account_detail.tab_claims')}</Tab>
          <Tab value="audit">{t('admin:claim.b2b_account_detail.tab_audit_log')}</Tab>
          {canViewMembers ? <Tab value="members">{t('admin:staff.tab.title')}</Tab> : null}
        </TabList>

        {/* Overview */}
        <TabPanel value="overview">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <div className="space-y-3 p-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-h4 font-semibold text-ink">
                    {t('admin:claim.b2b_account_detail.section_business')}
                  </h2>
                  {!editing && canWriteAccounts ? (
                    <Button size="xs" variant="ghost" onClick={() => setEditing(true)}>
                      {t('admin:claim.b2b_account_detail.edit_cta')}
                    </Button>
                  ) : null}
                </div>
                {editing ? (
                  <OverviewEditForm
                    account={account}
                    saving={saving}
                    serverError={editError}
                    nameLockError={nameLockError}
                    canOverrideNameLock={canOverrideNameLock}
                    onCancel={() => {
                      setEditing(false);
                      setNameLockError(null);
                    }}
                    onSave={(patch, override) => {
                      // A plain save drops the old lock notice; an override keeps
                      // the panel up (busy) while it resubmits.
                      if (!override) setNameLockError(null);
                      void handleSave(patch, override);
                    }}
                    onNameLockDismiss={() => setNameLockError(null)}
                  />
                ) : (
                  <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-[13px]">
                    <dt className="text-ink-3">
                      {t('admin:claim.b2b_account_detail.field_legal_name')}
                    </dt>
                    <dd className="text-ink">{account.legalBusinessName}</dd>
                    <dt className="text-ink-3">
                      {t('admin:claim.b2b_account_detail.field_display_name')}
                    </dt>
                    <dd className="text-ink">{account.displayName}</dd>
                    <dt className="text-ink-3">
                      {t('admin:claim.b2b_account_detail.field_country')}
                    </dt>
                    <dd className="text-ink">{account.countryCode}</dd>
                    <dt className="text-ink-3">
                      {t('admin:claim.b2b_account_detail.field_tax_id')}
                    </dt>
                    <dd className="text-ink">{account.taxId ?? '—'}</dd>
                  </dl>
                )}
              </div>
            </Card>

            <Card>
              <div className="space-y-4 p-4">
                <h2 className="text-h4 font-semibold text-ink">
                  {t('admin:claim.b2b_account_detail.section_owner')}
                </h2>
                {primaryOwner ? (
                  <div className="space-y-4">
                    {/* Identity */}
                    <div className="flex items-center gap-3">
                      <Avatar name={primaryOwner.displayName ?? primaryOwner.email} size="lg" />
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-[15px] font-semibold text-ink">
                          {primaryOwner.displayName ??
                            t('admin:claim.b2b_account_detail.owner_unnamed', {
                              defaultValue: 'Unnamed owner',
                            })}
                        </p>
                        <p className="truncate font-mono text-[12px] text-ink-3">
                          {primaryOwner.email}
                        </p>
                        <Badge tone={USER_STATUS_TONE[primaryOwner.status] ?? 'neutral'} size="sm">
                          {primaryOwner.status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </div>

                    {/* Roles */}
                    {primaryOwner.roles.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {primaryOwner.roles.map((role) => (
                          <Badge key={role} tone="info" size="sm">
                            {role.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    ) : null}

                    {/* Contact + tenure */}
                    <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-[13px] border-t border-hairline-2 pt-3">
                      <dt className="text-ink-3">
                        {t('admin:claim.b2b_account_detail.field_phone')}
                      </dt>
                      <dd className="font-mono text-[12px] text-ink">
                        {primaryOwner.phone ?? '—'}
                      </dd>
                      <dt className="text-ink-3">
                        {t('admin:claim.b2b_account_detail.owner_member_since', {
                          defaultValue: 'Member since',
                        })}
                      </dt>
                      <dd className="text-ink">{formatDate(primaryOwner.createdAt, locale)}</dd>
                    </dl>

                    {canViewUsers ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full"
                        onClick={() => navigate(`/users/${primaryOwner.id}`)}
                      >
                        {t('admin:common.view', { defaultValue: 'View user' })}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-[12.5px] text-ink-3">—</p>
                )}
              </div>
            </Card>
          </div>
          {/* F09 v2 — club / organizer accounts: tick, owner state + deep links.
              A failed summary load shows an error + retry, never a silent gap. */}
          {verificationFailed ? (
            <div className="mt-4 space-y-2" data-testid="b2b-account-verification-error">
              <Banner tone="danger">{t('admin:verification.common.loadError')}</Banner>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setVerificationReload((n) => n + 1)}
                data-testid="b2b-account-verification-retry"
              >
                {t('admin:verification.common.retry')}
              </Button>
            </div>
          ) : verificationSummary !== null ? (
            <div className="mt-4">
              <EntityVerificationCard
                entityType={verificationSummary.entityType}
                entityId={verificationSummary.entityId}
                verified={accountVerified}
                verifiedAt={account.verifiedAt ?? verificationSummary.verifiedAt}
                state={verificationSummary.verificationState}
                openRequest={verificationSummary.openRequest}
                testId="b2b-account-verification-card"
              />
            </div>
          ) : null}
        </TabPanel>

        {/* Venues */}
        <TabPanel value="venues">
          {venues.length === 0 ? (
            <EmptyState
              icon={<UIIcon name="pin" size={36} color="currentColor" />}
              title={t('admin:claim.b2b_account_detail.venues_tab.empty_title')}
              description={t('admin:claim.b2b_account_detail.venues_tab.empty_body')}
            />
          ) : (
            <Table
              data={venues as Venue[]}
              columns={venueColumns as TableColumn<Venue>[]}
              rowKey={(row) => row.id}
            />
          )}
        </TabPanel>

        {/* Claims */}
        <TabPanel value="claims">
          {claims.length === 0 ? (
            <EmptyState
              icon={<UIIcon name="receipt" size={36} color="currentColor" />}
              title={t('admin:claim.b2b_account_detail.claims_tab.empty_title')}
              description={t('admin:claim.b2b_account_detail.claims_tab.empty_body')}
            />
          ) : (
            <Table
              data={claims as VenueClaim[]}
              columns={claimColumns as TableColumn<VenueClaim>[]}
              rowKey={(row) => row.id}
              onRowPress={(row) => navigate(`/venue-claims/${row.id}`)}
            />
          )}
        </TabPanel>

        {/* Audit log */}
        <TabPanel value="audit">
          {auditScopedToSelf ? (
            <Banner tone="info" className="mb-4">
              {t('admin:claim.b2b_account_detail.audit_tab.scope_note')}
            </Banner>
          ) : null}
          {auditError ? <Banner tone="danger">{auditError}</Banner> : null}
          {auditLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size={24} />
            </div>
          ) : auditRows.length === 0 ? (
            <EmptyState
              icon={<UIIcon name="receipt" size={36} color="currentColor" />}
              title={t('admin:claim.b2b_account_detail.audit_tab.empty_title')}
              description={t('admin:claim.b2b_account_detail.audit_tab.empty_body')}
            />
          ) : (
            <Table
              data={auditRows as AdminAuditEntry[]}
              columns={auditColumns as TableColumn<AdminAuditEntry>[]}
              rowKey={(row) => row.id}
            />
          )}
        </TabPanel>

        {/* F13 T26 — Members tab (read-only) */}
        {canViewMembers ? (
          <TabPanel value="members">
            <B2bAccountMembersTab accountId={account.id} />
          </TabPanel>
        ) : null}
      </Tabs>

      {/* Dialogs */}
      <SuspendDialog
        open={suspendOpen}
        loading={suspendLoading}
        serverError={suspendError}
        onClose={() => setSuspendOpen(false)}
        onSubmit={(reason) => void handleSuspend(reason)}
      />
      <ResumeDialog
        open={resumeOpen}
        loading={resumeLoading}
        serverError={resumeError}
        onClose={() => setResumeOpen(false)}
        onSubmit={(note) => void handleResume(note)}
      />
      <ReassignOwnerDialog
        open={reassignVenue !== null}
        venueName={reassignVenue?.name ?? ''}
        currentAccountId={account.id}
        loading={reassignLoading}
        serverError={reassignError}
        onClose={() => setReassignVenue(null)}
        onSubmit={(body) => void handleReassign(body)}
      />
    </div>
  );
}
